// Formulário de questionário (novo e editar de /c/quiz): estado da tela, validação
// por campo e o corpo que a API recebe. Cada pergunta e cada opção têm uma `key`
// local, estável enquanto a tela está aberta, para os ids dos campos e as keys do React.

// Limite das colunas `quiz.title` e `quiz_questions.description` (varchar 255)
export const QUIZ_TITLE_MAX = 255;
export const QUIZ_QUESTION_MAX = 255;

export const QUESTION_TYPES = [
  { label: "Escrita", value: "desc" },
  { label: "Múltipla escolha", value: "multi" },
];

let lastKey = 0;
const nextKey = () => ++lastKey;

export const emptyOption = () => ({ key: nextKey(), title: "", isCorrect: false });

/** Pergunta nova: sem tipo (a pessoa escolhe, como na tela antiga) e com uma opção vazia. */
export const emptyQuestion = () => ({
  key: nextKey(),
  id: null,
  description: "",
  type: "",
  options: [emptyOption()],
  answersCount: 0,
});

export const emptyQuiz = () => ({ title: "", active: true, questions: [emptyQuestion()] });

/** Ids dos campos, usados nos erros e no foco. */
export const quizFieldId = {
  title: () => "quiz-title",
  description: (question) => `quiz-q${question.key}-description`,
  type: (question) => `quiz-q${question.key}-type`,
  option: (question, option) => `quiz-q${question.key}-o${option.key}`,
  options: (question) => `quiz-q${question.key}-options`,
};

/**
 * Questionário do `GET /quiz/:id` → estado da tela. As opções antigas trazem um
 * `question_type` solto e, na pergunta escrita, uma opção vazia: ficam de fora.
 */
export function hydrateQuiz(data) {
  const questions = [...(data?.questions || [])]
    .sort((a, b) => a.id - b.id)
    .map((q) => {
      const type = q.question_type === "desc" || q.question_type === "multi" ? q.question_type : "";
      const options =
        type === "multi" && Array.isArray(q.options) && q.options.length
          ? q.options.map((o) => ({ key: nextKey(), title: o?.title || "", isCorrect: !!o?.isCorrect }))
          : [emptyOption()];
      return {
        key: nextKey(),
        id: q.id,
        description: q.description || "",
        type,
        options,
        answersCount: Number(q.answersCount) || 0,
      };
    });

  return {
    title: data?.title || "",
    active: data?.status !== "INATIVO",
    questions: questions.length ? questions : [emptyQuestion()],
  };
}

/** Erros por id do campo, na ordem da tela (vazio quando está tudo certo). */
export function validateQuiz(values) {
  const errors = {};
  if (!values.title.trim()) errors[quizFieldId.title()] = "Informe o título.";

  values.questions.forEach((question) => {
    if (!question.description.trim()) errors[quizFieldId.description(question)] = "Escreva a pergunta.";
    if (!question.type) errors[quizFieldId.type(question)] = "Escolha o tipo de resposta.";
    if (question.type !== "multi") return;
    if (!question.options.length) {
      errors[quizFieldId.options(question)] = "Adicione pelo menos uma opção.";
      return;
    }
    question.options.forEach((option) => {
      if (!option.title.trim()) errors[quizFieldId.option(question, option)] = "Preencha a opção.";
    });
  });

  return errors;
}

/**
 * Corpo do criar e do editar: o da tela antiga, com o status e sem o `question_type`
 * solto nas opções. A pergunta escrita vai sem opções. Pergunta já salva leva o `id`
 * (o back atualiza as que vêm com id e apaga as que ficaram de fora).
 */
export function quizPayload(values) {
  return {
    title: values.title.trim(),
    status: values.active ? "ATIVO" : "INATIVO",
    questions: values.questions.map((question) => ({
      ...(question.id ? { id: question.id } : {}),
      description: question.description.trim(),
      question_type: question.type,
      options:
        question.type === "multi"
          ? question.options.map((option) => ({ title: option.title.trim(), isCorrect: !!option.isCorrect }))
          : [],
    })),
  };
}

/** Status da API → chave do `DomainStatus questionario`. Sem status, ativo (o padrão da coluna). */
export const quizStatusKey = (status) => (status === "INATIVO" ? "inativo" : "ativo");

/** "Nenhuma", "1 pessoa", "3 pessoas". */
export function respondentsLabel(count) {
  const n = Number(count) || 0;
  if (!n) return "Nenhuma";
  return `${n} ${n === 1 ? "pessoa" : "pessoas"}`;
}

/** "1 pergunta", "3 perguntas". */
export function questionsLabel(count) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? "pergunta" : "perguntas"}`;
}
